/**
 * pendantBloomPipeline — a zero-gravity liquid droplet that breathes
 * and splits via surface tension.
 *
 * A single large droplet in the center of the screen, with two
 * smaller satellite droplets that orbit it. Each droplet is a 2D SDF
 * (circle) perturbed by a low-frequency 2D wave that mimics surface
 * tension. The droplets combine with smooth-min so they merge when
 * close (like real liquid).
 *
 * Innovations:
 *   - Surface tension perturbation: droplet boundary is a circle
 *     perturbed by 5 sinusoidal modes.
 *   - Smooth-min merge: when droplets approach, they morph into a
 *     dumbbell shape.
 *   - Refraction: inside the droplet, the background is sampled
 *     with a screen-space offset proportional to the SDF gradient.
 *   - Iridescent skin where the wave amplitude is high.
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface PendantBloomPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float wave(vec2 p, float t, float seed) {
  float r = 0.0;
  r += 0.018 * sin(p.x * 9.0 + t * 1.7 + seed);
  r += 0.012 * sin(p.y * 11.0 + t * 2.3 + seed * 1.3);
  r += 0.015 * sin((p.x + p.y) * 7.0 - t * 1.5 + seed * 0.7);
  r += 0.010 * sin((p.x - p.y) * 13.0 + t * 2.9 + seed * 2.1);
  r += 0.008 * sin(length(p) * 17.0 + t * 3.5 + seed * 0.4);
  return r;
}

float smin(float a, float b, float k) {
  float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
  return mix(b, a, h) - k * h * (1.0 - h);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  float r = length(p);
  vec3 bg = mix(
    vec3(0.08, 0.10, 0.18),
    vec3(0.02, 0.02, 0.06),
    smoothstep(0.0, 0.7, r)
  );
  float dust = 0.0;
  for (int i = 0; i < 5; i++) {
    float fi = float(i);
    vec2 sp = vec2(
      sin(uTime * 0.3 + fi * 1.7),
      cos(uTime * 0.27 + fi * 2.1)
    ) * 0.85;
    dust += exp(-length(p - sp) * 25.0) * 0.06;
  }
  bg += vec3(0.7, 0.8, 1.0) * dust;

  float R0 = 0.22 + bass * 0.04 + 0.008 * sin(uTime * 1.5);
  float rWobble = wave(p, uTime, 0.0);
  float d0 = length(p) - (R0 + rWobble);

  float orbitT = uTime * (0.35 + high * 0.7);
  float orbitR = 0.55 + 0.08 * sin(uTime * 0.4);
  vec2 s1 = vec2(cos(orbitT), sin(orbitT)) * orbitR;
  vec2 s2 = vec2(cos(orbitT + 3.0), sin(orbitT + 3.0)) * orbitR;
  float Rs = 0.07 + 0.01 * sin(uTime * 2.0);
  float d1 = length(p - s1) - (Rs + wave(p - s1, uTime, 1.0));
  float d2 = length(p - s2) - (Rs + wave(p - s2, uTime, 2.0));

  float k = 0.015 + mid * 0.01;
  float d = smin(smin(d0, d1, k), d2, k);

  vec3 insideColor = mix(
    vec3(0.10, 0.40, 0.85),
    vec3(0.20, 0.85, 0.95),
    smoothstep(-0.3, 0.05, d)
  );
  insideColor = mix(insideColor, vec3(0.85, 0.95, 1.0),
                    smoothstep(-0.1, 0.0, d));
  float irid = 0.5 + 0.5 * sin(rWobble * 80.0 + uTime * 2.0);
  vec3 iridColor = vec3(
    0.5 + 0.5 * sin(irid * 6.2831 + 0.0),
    0.5 + 0.5 * sin(irid * 6.2831 + 2.094),
    0.5 + 0.5 * sin(irid * 6.2831 + 4.189)
  );
  insideColor = mix(insideColor, iridColor, smoothstep(0.0, 0.04, d) * 0.4);

  float eps = 0.005;
  float dx = (wave(p + vec2(eps, 0.0), uTime, 0.0) - wave(p - vec2(eps, 0.0), uTime, 0.0)) / (2.0 * eps);
  float dy = (wave(p + vec2(0.0, eps), uTime, 0.0) - wave(p - vec2(0.0, eps), uTime, 0.0)) / (2.0 * eps);
  vec2 refract = -normalize(vec2(dx, dy) + 0.0001) * 0.04;
  float r2 = length(p + refract);
  vec3 refractedBg = mix(
    vec3(0.10, 0.20, 0.40),
    vec3(0.30, 0.50, 0.85),
    smoothstep(0.0, 0.5, r2)
  );

  float ring = smoothstep(0.0, 0.005, d) - smoothstep(0.005, 0.025, d);
  vec3 highlight = vec3(1.0) * ring * (0.6 + 0.4 * high);

  float outer = exp(-max(d, 0.0) * 14.0);
  vec3 glow = vec3(0.4, 0.7, 1.0) * outer * (0.4 + 0.6 * bass);

  vec3 col = bg;
  if (d < 0.05) {
    float mixT = smoothstep(0.05, -0.05, d);
    col = mix(refractedBg, insideColor, mixT);
  }
  col += highlight;
  col += glow * 0.5;

  float sparkle = smoothstep(0.012, 0.020, abs(rWobble));
  col += vec3(1.0, 0.85, 0.7) * sparkle * 0.6;

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

export function computePendantBloomBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.55);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.85 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.15 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.35 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.65 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.95 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.35 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.75 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.25 + 3.0);
}

export function computePendantBloomRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createPendantBloomPipeline(gl: WebGL2RenderingContext): PendantBloomPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawPendantBloom(
  gl: WebGL2RenderingContext,
  pipe: PendantBloomPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
